import type { ChloromanceTier, Plant, PlantForm, WeatherLocation } from './types';

export const STORAGE_KEY = 'chloromance-plant-tracker-plants';
export const REMINDERS_KEY = 'chloromance-plant-tracker-reminders';
export const NOTIFIED_KEY = 'chloromance-plant-tracker-notified-states';
export const WEATHER_LOCATION_KEY = 'chloromance-plant-tracker-weather-location';
export const THEME_KEY = 'chloromance-plant-tracker-theme';
export const FREE_TIER_PLANT_LIMIT = 20;
export const GROWER_TIER_PLANT_LIMIT = 50;
export const PLANT_TIERS: ChloromanceTier[] = [
  {
    id: 'free',
    name: 'Free',
    plantLimit: FREE_TIER_PLANT_LIMIT,
    description: 'For smaller shelves and starter collections.',
  },
  {
    id: 'grower',
    name: 'Grower',
    plantLimit: GROWER_TIER_PLANT_LIMIT,
    description: 'For fuller homes, patios, and greenhouse corners.',
  },
  {
    id: 'unlimited',
    name: 'Unlimited',
    plantLimit: null,
    description: 'For whole-collection tracking with no plant cap.',
  },
];
export const DEFAULT_PLANT_TIER_ID = 'free';
export const PLANT_LIMIT_NOTE = `Free tier includes up to ${FREE_TIER_PLANT_LIMIT} plants. Upgrade for ${GROWER_TIER_PLANT_LIMIT} plants or unlimited plants.`;
export const DAY_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_ZONES = [
  'Balcony / Patio',
  'Bathroom',
  'Bedroom',
  'Office',
  'Home Office',
  'Floor',
  'Greenhouse',
  'Greenhouse Storage',
  'Kitchen Windowsill',
  'Living Room',
  'Office Desk',
  'Propagation Bench',
  'Shady Corner',
  'Sunny Wall',
  'Flower Bed',
  'Pots',
  'Planters'
];

export const DEFAULT_WEATHER_LOCATION: WeatherLocation = {
  latitude: '53.3498',
  longitude: '-6.2603',
};

export const OUTDOOR_LOCATIONS = new Set([
  'Balcony / Patio',
  'Greenhouse',
  'Greenhouse Storage',
  'Propagation Bench',
  'Shady Corner',
  'Sunny Wall',
  'Flower Bed',
  'Pots',
  'Planters'
]);

export const INDOOR_LOCATIONS = new Set(['Bathroom', 'Bedroom', 'Kitchen Windowsill', 'Living Room', 'Office Desk']);

export const PLANT_TYPES = [
  'Tomato',
  'Basil',
  'Pepper',
  'Fern',
  'Cucumber',
  'Lettuce',
  'Marigold',
  'Grass',
  'Potato',
  'Fennel',
  'Strawberry',
  'Mint',
  'Monstera',
  'Pothos',
  'Snake Plant',
  'Orchid',
  'Succulent',
  'Other',
];

export const COMPANION_RULES: Record<string, { companions: string[]; antagonists: string[] }> = {
  Tomato: { companions: ['Basil', 'Marigold', 'Lettuce'], antagonists: ['Potato', 'Fennel'] },
  Basil: { companions: ['Tomato', 'Pepper'], antagonists: ['Fennel'] },
  Pepper: { companions: ['Basil', 'Marigold', 'Tomato'], antagonists: ['Fennel'] },
  Cucumber: { companions: ['Marigold', 'Lettuce'], antagonists: ['Potato'] },
  Lettuce: { companions: ['Tomato', 'Cucumber', 'Strawberry'], antagonists: ['Fennel'] },
  Marigold: { companions: ['Tomato', 'Pepper', 'Cucumber'], antagonists: [] },
  Grass: { companions: [], antagonists: [] },
  Potato: { companions: [], antagonists: ['Tomato', 'Cucumber'] },
  Fennel: { companions: [], antagonists: ['Tomato', 'Basil', 'Pepper', 'Lettuce'] },
  Strawberry: { companions: ['Lettuce', 'Basil'], antagonists: ['Fennel'] },
  Mint: { companions: ['Tomato'], antagonists: ['Basil'] },
  Monstera: { companions: [], antagonists: [] },
  Pothos: { companions: [], antagonists: [] },
  'Snake Plant': { companions: [], antagonists: [] },
  Orchid: { companions: [], antagonists: [] },
  Succulent: { companions: [], antagonists: [] },
  Fern: { companions: [], antagonists: [] },
  Other: { companions: [], antagonists: [] },
};

export const STARTER_PLANTS: Plant[] = [
  {
    id: 'plant-roma-tomato',
    name: 'Roma Tomato',
    variety: 'Solanum lycopersicum',
    plant_type: 'Tomato',
    plant_count: 1,
    chloromance_zone: 'Balcony / Patio',
    watering_frequency_days: 2,
    last_watered_timestamp: Date.now() - Math.round(DAY_MS * 1.62),
    note: 'Watch for blossom end rot during hot spells.',
  },
  {
    id: 'plant-basil',
    name: 'Genovese Basil',
    variety: 'Ocimum basilicum',
    plant_type: 'Basil',
    plant_count: 1,
    chloromance_zone: 'Kitchen Windowsill',
    watering_frequency_days: 1,
    last_watered_timestamp: Date.now() - Math.round(DAY_MS * 0.42),
    note: 'Pinch flowers early.',
  },
  {
    id: 'plant-fern',
    name: 'Maidenhair Fern',
    variety: 'Adiantum',
    plant_type: 'Fern',
    plant_count: 1,
    chloromance_zone: 'Bathroom',
    watering_frequency_days: 3,
    last_watered_timestamp: Date.now() - Math.round(DAY_MS * 3.25),
    note: 'Mist lightly after watering.',
  },
];

export const EMPTY_FORM: PlantForm = {
  name: '',
  variety: '',
  plant_type: 'Tomato',
  plant_count: '1',
  chloromance_zone: DEFAULT_ZONES[0],
  watering_frequency_days: '2',
  note: '',
  sample_id: '',
  catalogue_type: '',
  suggested_location: '',
  fruit_or_flower_months: [],
  best_harvest_months: [],
  care_profile: null,
};
